"use client";
import { motion } from "motion/react";
import IconLink from "../General Components/IconLink";
import SkillIcon from "../About Me/SkillIcon";
import { useFilter } from "@/app/context/FilterContext";

export interface GameSkill {
  id: number;
  name: string;
  svgData: string | null;
  color: string;
  invert: boolean;
}

export interface GameMediaItem {
  id: number;
  type: string;
  mimeType: string;
  fileName: string;
}

export interface GameRow {
  id: number;
  name: string;
  year: string;
  description: string;
  skills: GameSkill[];
  itchUrl: string | null;
  githubUrl: string | null;
  media: GameMediaItem[];
  filters: { id: number; name: string }[];
}

interface Props {
  game: GameRow;
}

const GameCard = ({ game }: Props) => {
  const { activeFilter } = useFilter();
  const filterMatch = activeFilter
    ? game.filters.some((f) => f.name === activeFilter)
    : null;

  return (
    <div style={{
      boxShadow: filterMatch === true ? "0 0 28px 6px var(--filter-glow-color)" : "0 0 0px 0px transparent",
      opacity: filterMatch === false ? 0.3 : 1,
      transition: "opacity 0.5s ease-out, box-shadow 0.5s ease-out",
      borderRadius: "16px",
    }}>
    <motion.div
      id={String(game.id)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="bg-(--background2) rounded-2xl p-8 border border-(--uforeground2)"
    >
      {/* Header row: title left, year + actions right */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-1">
        <div className="flex-1 min-w-0">
          <h2 className="text-4xl font-semibold">{game.name}</h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-(--uforeground2) h-0.5 mt-2"
            style={{ transformOrigin: "left" }}
          />
        </div>

        <div className="flex items-center gap-2 flex-none">
          <span className="text-(--uforeground) text-sm font-medium mr-1">
            {game.year}
          </span>
          {game.githubUrl && (
            <IconLink
              link={game.githubUrl}
              image="/Icons/Websites/Github-Icon.svg"
              imgClassName="flex-none h-7"
              label={`${game.name} GitHub`}
            />
          )}
          {game.itchUrl && (
            <IconLink
              link={game.itchUrl}
              image="/Icons/Websites/Itch-Icon.svg"
              imgClassName="flex-none h-7"
              label={`${game.name} Itch.io`}
            />
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-(--foreground2) mt-4 leading-relaxed whitespace-pre-line">
        {game.description}
      </p>

      {/* Media */}
      {game.media.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2 mt-6">
          {game.media.map((item) =>
            item.type === "image" ? (
              <img
                key={item.id}
                src={`/api/games/${game.id}/media/${item.id}`}
                alt={item.fileName}
                className="h-48 rounded-xl flex-none object-cover"
              />
            ) : (
              <video
                key={item.id}
                src={`/api/games/${game.id}/media/${item.id}`}
                autoPlay
                muted
                loop
                playsInline
                className="h-48 rounded-xl flex-none object-cover"
              />
            )
          )}
        </div>
      )}

      {/* Skill badges */}
      {game.skills.length > 0 && (
        <div className="flex flex-wrap mt-6">
          {game.skills.map((skill) => (
            <SkillIcon
              key={skill.id}
              imageIcon={
                skill.svgData
                  ? `data:image/svg+xml,${encodeURIComponent(skill.svgData)}`
                  : undefined
              }
              shadowColor={skill.color}
              invert={skill.invert}
            >
              {skill.name}
            </SkillIcon>
          ))}
        </div>
      )}
    </motion.div>
    </div>
  );
};

export default GameCard;
